/**
 * Centralized API Error Handler
 * Standardizes error responses across the application
 */

import NotificationService from "../components/alerts";

/**
 * Standard error response format
 * @typedef {Object} ErrorResponse
 * @property {number} status - HTTP status code
 * @property {string} message - Error message to display
 * @property {Object} data - Error data from server
 * @property {boolean} isError - Flag to indicate this is an error
 */

/**
 * Handles API errors and shows appropriate notifications
 * @param {Error|Object} error - The error object from axios
 * @param {string} defaultMessage - Default message if error message not available
 * @param {{ notify?: boolean }} [options] - notify: false skips the global toast
 * @returns {ErrorResponse} - Standardized error response
 */
export const handleApiError = (
  error,
  defaultMessage = "An error occurred",
  options = {}
) => {
  const { notify = true } = options;
  let status = 500;
  let message = defaultMessage;
  let data = null;

  // Handle case where error is an axios response (rejected by interceptor)
  if (error.status) {
    status = error.status;
    data = error.data || {};
  }
  // Handle case where error is an error object with response
  else if (error.response) {
    status = error.response.status;
    data = error.response.data || {};
  }
  // Handle network errors
  else if (error.message === "Network Error" || !navigator.onLine) {
    status = 0;
    message = "Network error. Please check your connection.";
  }
  // Fallback for unknown errors
  else {
    message = error.message || defaultMessage;
  }

  // Determine message based on status code
  if (status === 422) {
    // Validation errors
    if (data.validationErrors) {
      const errorStrings = Object.values(data.validationErrors).filter(Boolean);
      message = errorStrings.join(" ") || "Validation failed";
    } else if (data.error) {
      message = data.error;
    } else {
      message = "Validation failed";
    }
  } else if (status === 401) {
    // Unauthorized - handled by interceptor
    message = "Your session has expired. Please login again.";
  } else if (status === 403) {
    // Forbidden
    message = data.error || "You don't have permission to perform this action";
  } else if (status === 404) {
    // Not found
    message = data.error || "Resource not found";
  } else if (status >= 500) {
    // Server errors
    message = data.error || "Server error. Please try again later.";
  } else if (status >= 400) {
    // Other client errors
    message = data.error || defaultMessage;
  } else if (status === 0) {
    // Already set above
  } else {
    message = data.error || defaultMessage;
  }

  // Create standardized response
  const errorResponse = {
    status,
    message,
    data,
    isError: true,
  };

  // Notify user unless the caller renders an inline message
  if (notify) {
    NotificationService.error(message);
  }

  return errorResponse;
};

/**
 * Standardizes API responses
 * @param {Object} response - Response from API
 * @param {string} successMessage - Optional success message
 * @returns {Object} - Standardized response
 */
export const handleApiResponse = (response, successMessage = null) => {
  if (successMessage) {
    NotificationService.success(successMessage);
  }

  return {
    status: response.status,
    data: response.data,
    isError: false,
  };
};
